import { ApolloError } from '@apollo/client/errors'
import { TFunction } from 'gatsby-plugin-react-i18next'
import { ja, enUS } from 'date-fns/locale'
import { format, differenceInMinutes, parse, differenceInDays } from 'date-fns'
import base64 from 'base-64'
import { AsYouType } from 'libphonenumber-js'
import {
  BookingDate,
  OrderApproval,
  OrderQuery,
  OrderPurpose,
  Period,
  PrefectureCode,
  ProductStatus,
  ProductQuery,
  CustomerType,
  NotForSalesQuery,
  BookingDateInput,
  BookingStatusListQuery,
} from 'lib/graphql/storefront'
import { SAGA_ARENA_PRODUCT_NAME, SAGA_ARENA_USAGE_TYPE } from './sagaArena'

export enum EditableStatus {
  possible = 'possible',
  expired = 'expired',
  canceled = 'canceled',
  disapproved = 'disapproved',
  approvalRequired = 'approval-required',
  other = 'other',
}

export const defaultLanguage = 'jp'
export const currencyJapan = 'JPY'
export const numberFormat = 'ja-JP'
export const dateFormat = 'yyyy-MM-dd'

export const decodeBase64 = (value: string) => {
  if (!value) return ''

  const decoded = base64.decode(value).split(':')[1]
  // すでに decode 済であればそのまま return
  if (!decoded?.[1]) return value
  else return decoded
}

// DropDown コンポーネントで使う options を返す
export const i18nPurposeOptions = (
  t: TFunction<'translation', undefined>
): Array<{ id: string; name: string }> => {
  // 一般利用とイベント利用の二つに制限（TODO: 必要であれば元に戻す）
  // return Object.entries(OrderPurpose).map(([_, value]) => ({
  //   id: value,
  //   name: t(value),
  // }))
  return [
    {
      id: OrderPurpose.General,
      name: t(`share.${OrderPurpose.General}`),
    },
    {
      id: OrderPurpose.Event,
      name: t(`share.${OrderPurpose.Event}`),
    },
  ]
}

export const CalcTimeDifferenceInMinutes = (period: Period): number => {
  const date = new Date()
  const day = [date.getFullYear(), date.getMonth(), date.getDate()]
  const start = String(period.start)
    .split(':')
    .map((value) => Number(value))
  const end = String(period.end)
    .split(':')
    .map((value) => Number(value))
  return differenceInMinutes(
    new Date(day[0], day[1], day[2], end[0], end[1], end[2]),
    new Date(day[0], day[1], day[2], start[0], start[1], start[2])
  )
}

const priceFormatter = new Intl.NumberFormat(numberFormat, {
  style: 'currency',
  currency: currencyJapan,
})

export const priceFormat = (price: number) => {
  return priceFormatter.format(price)
}

export const convertDateToDateInput = (date: Date): string => {
  if (!date) return ''
  return format(date, dateFormat)
}

export const convertDate = (date: string): string => {
  if (!date) {
    return ''
  }
  return format(new Date(date), 'yoMMMdo', { locale: ja })
}

export const convertDateTime = (datetime: string): string => {
  if (!datetime) return ''
  return format(new Date(datetime), 'yoMMMdo HH:mm', { locale: ja })
}

export const convertTime = (time: string): string => {
  if (!time) return ''
  const times = time.split(':')
  return `${Number(times[0])}時${times[1]}分`
}

export const convertDateOrder = (date: string): string => {
  const d = new Date(date)
  let month = '' + (d.getMonth() + 1)
  let day = '' + d.getDate()
  const year = d.getFullYear()

  if (month.length < 2) month = '0' + month
  if (day.length < 2) day = '0' + day

  return [year, month, day].join('-')
}
export const convertScheduled = (
  startDate: string,
  startTime: string,
  endTime: string
): string => {
  return `${convertDate(startDate)} ${startTime.substr(
    0,
    5
  )} ~ ${endTime.substr(0, 5)}`
}

export const convertLineItems = (
  lineItems: OrderQuery['order']['lineItems']
): string => {
  let result = ''
  if (lineItems?.edges?.length < 5) {
    for (let i = 0; i < lineItems?.edges?.length; i++) {
      const { lineItem } = lineItems?.edges[i]
      if (result)
        result = result + '/' + lineItem.name?.jaJp + lineItem.quantity
      else result = lineItem.name?.jaJp + lineItem.quantity
    }
    return result
  }
  for (let i = 0; i < 5; i++) {
    const { lineItem } = lineItems?.edges[i]
    if (result) result = result + '/' + lineItem.name + lineItem.quantity
    else result = lineItem.name?.jaJp + lineItem.quantity
  }
  result += `他 ${lineItems?.edges?.length - 5}点`
  return result
}

export const compareDateGreatThan = (date: string): boolean => {
  return new Date() > new Date(date)
}

export const getDateFurthest = (dates: string[], reverse?: boolean) => {
  if (!dates?.length) {
    return null
  }
  let result = dates[0]
  for (let i = 0; i < dates.length; i++) {
    if (reverse) {
      if (dates[i] < result) {
        result = dates[i]
      }
    } else {
      if (dates[i] > result) {
        result = dates[i]
      }
    }
  }
  return result
}

export const getDateFormat = (date?: string): string => {
  const formatDate = date ? new Date(date) : new Date()
  return (
    formatDate.getFullYear() +
    '-' +
    (formatDate.getMonth() > 8
      ? formatDate.getMonth() + 1
      : '0' + (formatDate.getMonth() + 1)) +
    '-' +
    (formatDate.getDate() > 9
      ? formatDate.getDate()
      : '0' + formatDate.getDate())
  )
}

export const displayDate = (
  bookingDates: OrderQuery['order']['bookingDates']
) => {
  if (!bookingDates?.length) {
    return ''
  }
  const dateFurthest = getDateFurthest(bookingDates.map((item) => item.date))
  const dateNearest = getDateFurthest(
    bookingDates.map((item) => item.date),
    true
  )
  if (dateFurthest === dateNearest) {
    return dateFurthest
  }
  return `${dateNearest} ~ ${dateFurthest}`
}

export const exportDateInput = (bookingDates: BookingDate[]) => {
  if (!bookingDates?.length) {
    return { start: '', end: '' }
  }
  const dateFurthest = getDateFurthest(bookingDates.map((item) => item.date))
  const dateNearest = getDateFurthest(
    bookingDates.map((item) => item.date),
    true
  )
  return {
    start: dateNearest,
    end: dateFurthest,
  }
}

export const getEndDate = (bookingDates: BookingDate[]) => {
  if (!bookingDates?.length) {
    return ''
  }
  const dateFurthest = new Date(
    getDateFurthest(bookingDates.map((item) => item.date))
  )
  return dateFurthest.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

// 予約の編集可否判定
export const editablePermission = (
  order: OrderQuery['order']
): EditableStatus => {
  // 注文がキャンセルされている場合、ステータスを "canceled" として返す
  if (order?.canceledAt) return EditableStatus.canceled
  // 予約日がない場合、ステータスを "other" として返す
  if (!order?.bookingDates?.length) return EditableStatus.other
   // 承認必須施設の場合、一切の更新、キャンセルを不可とする
  if (order?.approvalRequired) return EditableStatus.approvalRequired
  // 注文が承認されていない場合、ステータスを "disapproved" として返す
  if (order?.approval === OrderApproval.Disapproved)
    return EditableStatus.disapproved
  // 予約日の中で最も未来の日付を取得
  const dateFurthest = getDateFurthest(
    order?.bookingDates.map(({ date }) => date)
  )
  const daysDifference = differenceInDays(new Date(dateFurthest), new Date())
  // 承認不要施設で利用日が一か月より先の予約であれば変更、キャンセル可
  if (order?.approvalRequired === false && daysDifference > 30)
    return EditableStatus.possible;
  // 承認不要施設でも利用日が一か月以内であれば変更、キャンセル不可
  if (order?.approvalRequired === false && daysDifference <= 30)
    return EditableStatus.expired
  // 最も未来の日付が現在の日付よりも過去の場合、ステータスを "expired" として返す
  if (compareDateGreatThan(dateFurthest)) return EditableStatus.expired
  // それ以外の場合、ステータスを "possible" として返す
  else return EditableStatus.possible
}

export const getTimeBooking = (
  bookingDates: OrderQuery['order']['bookingDates'],
  period: Period
) => {
  if (!bookingDates?.length) {
    return ''
  }
  let dates = convertDate(bookingDates[0].date)
  if (bookingDates.length > 1)
    dates += ` ~ ${convertDate(bookingDates[bookingDates.length - 1].date)}`

  return `${dates} ${period.start.slice(0, 5)} - ${period.end.substr(0, 5)}`
}

export interface ZipResult {
  address1?: string
  address2?: string
  address3?: string
  kana1?: string
  kana2?: string
  kana3?: string
  prefcode?: string
  zipcode?: string
}

export const searchByZip = async (zip: string) => {
  const result = await fetch(
    `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zip}`
  ).then(async (res) => {
    const r = await res.json()
    let message_1 = ''
    if (r?.message) {
      switch (r?.message) {
        case 'パラメータ「郵便番号」の桁数が不正です。':
          message_1 = 'errors.address-search-error-zipcode-number'
          break
        case '必須パラメータが指定されていません。':
          message_1 = 'errors.address-search-error-zipcode-empty'
          break
      }
    } else {
      if (!r?.results)
        message_1 = 'errors.address-search-error-zipcode-notfound'
    }

    return {
      results: r?.results,
      message: message_1,
    }
  })
  return result
}

export const convertPrefCode = (zipcode: string): PrefectureCode => {
  switch (zipcode) {
    case '01':
      return PrefectureCode.Hk
    case '02':
      return PrefectureCode.Ao
    case '03':
      return PrefectureCode.It
    case '04':
      return PrefectureCode.Mg
    case '05':
      return PrefectureCode.Ak
    case '06':
      return PrefectureCode.Yg
    case '07':
      return PrefectureCode.Fs
    case '09':
      return PrefectureCode.Ib
    case '10':
      return PrefectureCode.Gu
    case '11':
      return PrefectureCode.St
    case '12':
      return PrefectureCode.Cb
    case '13':
      return PrefectureCode.Ty
    case '14':
      return PrefectureCode.Kn
    case '15':
      return PrefectureCode.Ni
    case '16':
      return PrefectureCode.Tm
    case '17':
      return PrefectureCode.Is
    case '18':
      return PrefectureCode.Fi
    case '19':
      return PrefectureCode.Yn
    case '20':
      return PrefectureCode.Na
    case '21':
      return PrefectureCode.Gi
    case '22':
      return PrefectureCode.Sz
    case '23':
      return PrefectureCode.Ai
    case '24':
      return PrefectureCode.Me
    case '25':
      return PrefectureCode.Si
    case '26':
      return PrefectureCode.Ky
    case '27':
      return PrefectureCode.Os
    case '28':
      return PrefectureCode.Hg
    case '29':
      return PrefectureCode.Nr
    case '30':
      return PrefectureCode.Wa
    case '31':
      return PrefectureCode.Tt
    case '32':
      return PrefectureCode.Sn
    case '33':
      return PrefectureCode.Oy
    case '34':
      return PrefectureCode.Hs
    case '35':
      return PrefectureCode.Ya
    case '36':
      return PrefectureCode.Tk
    case '37':
      return PrefectureCode.Ka
    case '38':
      return PrefectureCode.Eh
    case '39':
      return PrefectureCode.Ko
    case '40':
      return PrefectureCode.Fo
    case '41':
      return PrefectureCode.Sg
    case '42':
      return PrefectureCode.Ns
    case '43':
      return PrefectureCode.Ku
    case '44':
      return PrefectureCode.Oi
    case '45':
      return PrefectureCode.Mz
    case '46':
      return PrefectureCode.Kg
    case '47':
      return PrefectureCode.Ok
  }
}

// form validation regular expressions
export const formValidationRegExp = {
  numberOfPeople: /^[1-9][0-9]{0,3}$/,
  mailAddress:
    /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]+.[A-Za-z0-9]+$/,
}

export const i18nProvinceOptions = (
  t: TFunction<'translation', undefined>
): Array<{ id: string; name: string }> => {
  return Object.entries(PrefectureCode).map(([_, value]) => ({
    id: value,
    name: t(`prefectureCode.${value}`),
  }))
}

// mypage order history filter DropDown コンポーネントで使う options を返す
export const i18nApprovalOptions = (
  t: TFunction<'translation', undefined>
): Array<{ id: string; name: string }> => {
  return [
    /** 空欄 */
    {
      id: 'BLANK',
      name: t('share.BLANK'),
    },
    /** 承認 */
    {
      id: OrderApproval.Approved,
      name: t(`share.${OrderApproval.Approved}`),
    },
    /** 非承認 */
    {
      id: OrderApproval.Disapproved,
      name: t(`share.${OrderApproval.Disapproved}`),
    },
    /** 承認待ち */
    {
      id: OrderApproval.Pending,
      name: t(`share.${OrderApproval.Pending}`),
    },
  ]
}

export const i18nCustomerTypeOptions = (
  t: TFunction<'translation', undefined>
): Array<{ id: string; name: string }> => {
  return [
    /** 法人 */
    {
      id: CustomerType.Corporate,
      name: t('share.customer-type.corporate'),
    },
    /** 個人 */
    {
      id: CustomerType.Personal,
      name: t('share.customer-type.personal'),
    },
  ]
}

// fomat the number of month for product's calendar
export const fomatMonthNumber = (month: number) => {
  if (month === 13) return 1
  if (month === 0) return 12
  return month
}

// caculate the number of year for product's calendar
export const calculateYearAndMonth = (
  type: string,
  year: number,
  month: number
) => {
  switch (type) {
    case 'previous':
      if (month === 0) return { year: year - 1, month: fomatMonthNumber(month) }
      return { year, month: fomatMonthNumber(month) }
    case 'next':
      if (month === 13)
        return { year: year + 1, month: fomatMonthNumber(month) }
      return { year, month: fomatMonthNumber(month) }
  }
}

// return string type year and month for product's calendar
export const getStringCurrentDate = (y?: number, m?: number, d?: number) => {
  const now = new Date()
  const year = String(y || now.getFullYear())
  const month =
    (m || now.getMonth() + 1) >= 10
      ? String(m || now.getMonth() + 1)
      : '0' + (m || now.getMonth() + 1)
  const date =
    (d || now.getDate()) >= 10
      ? String(d || now.getDate())
      : '0' + (d || now.getDate())
  return {
    year,
    month,
    date,
  }
}

// return full date of String type
export const getStringFullDate = (date: Date) => {
  const {
    year: y,
    month: m,
    date: d,
  } = getStringCurrentDate(
    new Date(date).getFullYear(),
    new Date(date).getMonth() + 1,
    new Date(date).getDate()
  )
  return `${y}-${m}-${d}`
}

export const handleApolloError = ({
  graphQLErrors,
  networkError,
  message,
}: ApolloError): string => {
  if (graphQLErrors?.length > 0) {
    // GraphQL でのエラー
    switch (graphQLErrors[0].extensions?.code) {
      case 'GRAPHQL_PARSE_FAILED':
      case 'GRAPHQL_VALIDATION_FAILED':
      case 'BAD_USER_INPUT':
      case 'UNAUTHENTICATED':
      case 'FORBIDDEN':
      case 'PERSISTED_QUERY_NOT_FOUND':
      case 'PERSISTED_QUERY_NOT_SUPPORTED':
        return `apollo.errors.${graphQLErrors[0].extensions?.code}`
      case 'INTERNAL_SERVER_ERROR':
      default:
        return 'apollo.errors.INTERNAL_SERVER_ERROR'
    }
  } else if (networkError) {
    // 400, 500系エラー
    if (message.includes('Received status code 400')) {
      return 'apollo.errors.input-error'
    } else {
      return 'apollo.errors.network-error'
    }
  } else {
    return message
  }
}

/**
 * 施設が販売停止・公開させるかどうかを判断
 * @param product 施設obj
 * @returns 販売できないかどうか （true - 販売停止）
 */
export const productIsNotForSale = (
  product: ProductQuery['product']
): boolean => {
  const productIsActive = product?.status === ProductStatus.Active
  const allVariantsAreNotForSale = product?.variants?.edges?.every(
    ({ variant }) => !variant?.availableForSale
  )
  // productのstatusがACTIVEで、全てvariantがnotForSale
  return allVariantsAreNotForSale && productIsActive
}

/**
 * ハイフンなしの電話番号をハイフン付きの形に整形する
 * 例：09012345678 → 090-1234-5678
 * 例：0312345678 → 03-1234-5678
 * 例：0952243151 → 0952-24-3151
 *
 * @param value ハイフン付きなしのnumber
 * @returns ハイフン付きのnumber
 */
export const formatTelNumber = (value: string): string =>
  new AsYouType('JP').input(value)

// 文字列「yyyy-MM-dd」の日付を「yyyy年MM月dd日」のようにする
const formatDateJA = (date: string): string =>
  format(parse(date, 'yyyy-MM-dd', new Date()), 'yyyy年MM月dd日', {
    locale: ja,
  })

// 文字列「yyyy-MM-dd」の日付を「yyyy/MM/dd/」のようにする
const formatDateEn = (date: string): string =>
  format(new Date(date), 'yyyy/MM/dd', { locale: enUS })

/**
 * 利用予定開始日・終了日の形＆他言語対応
 * ( ja: yyyy年MM月dd日  en: yyyy/MM/dd )
 * @param startDate 開始日
 * @param endDate　終了日
 * @param lang i18n
 * @returns
 */
export const getDateFormated = (
  // bookingDates: OrderQuery['order']['bookingDates'],
  startDate: string,
  endDate: string,
  lang: string
): { start: string; end: string } => {
  if (lang === 'jp') {
    return {
      start: formatDateJA(startDate),
      end: formatDateJA(endDate),
    }
  }
  return {
    start: formatDateEn(startDate),
    end: formatDateEn(endDate),
  }
}

/**
 * 利用予定開始日・終了日をフォーマット
 * @param bookingDates
 * @param lang i18n
 * @returns YYYY年MM月DD日 - YYYY年MM月DD日
 */
export const formatDate = (
  bookingDates: OrderQuery['order']['bookingDates'],
  lang: string
): string => {
  if (!bookingDates?.length) return ''

  const dates = bookingDates?.map((item: BookingDate) => item.date)
  const dataSourceStart = getDateFurthest(dates, true)
  const dataSourceEnd = getDateFurthest(dates)

  const { start, end } = getDateFormated(dataSourceStart, dataSourceEnd, lang)
  if (start === end) return start
  return start + ' - ' + end
}

/**
 * 利用時間帯をフォーマット
 * @param scheduledTime
 * @returns HH:MM - HH:MM
 */
export const formatTime = (scheduledTime: Period): string => {
  const start = scheduledTime?.start?.slice(0, 5)
  const end = scheduledTime?.end?.slice(0, 5)
  return start + ' - ' + end
}

/**
 *利用予定開始日・終了日 & 利用時間帯をフォーマット
 * @param bookingDates
 * @param scheduledTime
 * @param lang i18n
 * @returns YYYY年MM月DD日 HH:MM - YYYY年MM月DD日 HH:MM
 */
export const formatDateAndTime = (
  bookingDates: OrderQuery['order']['bookingDates'],
  scheduledTime: Period,
  lang: string
): string => {
  const dates = bookingDates?.map((item: BookingDate) => item.date)
  const dataSourceStart = getDateFurthest(dates, true)
  const dataSourceEnd = getDateFurthest(dates)

  const { start: startDate, end: endDate } = getDateFormated(
    dataSourceStart,
    dataSourceEnd,
    lang
  )

  const getTime = (time: string): string => {
    if (!time) return ''
    const times = time.split(':')

    return `${Number(times[0])}:${times[1]}`
  }

  const start = `${startDate} ${getTime(scheduledTime.start)}`
  const end = `${endDate} ${getTime(scheduledTime.end)}`

  if (bookingDates?.length > 1) return start + ' - ' + end

  return `${startDate} ${formatTime(scheduledTime)}`
}

/**
 * 施設の最低利用料金を取得する関数
 * @param product
 * @returns 最低利用料金（無効な場合は0を返す）
 */
export const getMinUsageFee = (product: ProductQuery['product']) => {
  let minUsageFee = 0

  // SAGAアリーナの場合は催事の料金を表示する
  if (product?.name?.jaJp === SAGA_ARENA_PRODUCT_NAME.MAIN || product?.name?.jaJp === SAGA_ARENA_PRODUCT_NAME.SUB) {
    minUsageFee = product?.variants?.edges.find(({ variant }) => variant.title.jaJp.includes(SAGA_ARENA_USAGE_TYPE.EVENT))?.variant?.price ?? 0
  } else {
    // 価格が0以上のものを抽出し、最小値を取得する
    const filteredPriceList = product?.variants?.edges?.filter(({ variant }) => variant?.price > 0).map(({ variant }) => variant?.price)
    minUsageFee = Math.min(...filteredPriceList)
  }

  // 数値であり、かつ有限なら税込みに変換して返す
  return (typeof minUsageFee === 'number' && isFinite(minUsageFee))
    ? minUsageFee
    : 0
}

/**
* 日付文字列をISO 8601形式に変換し、Invalid Dateを回避
* @param dateString // YYYY/MM/DD HH:mm:ss 形式の日付文字列
* @returns ISO 8601形式の日付文字列
*/
export const toISOFormat = (dateString: string): string => {
  const [datePart, timePart] = dateString.split(' ')
  const [year, month, day] = datePart.split('/')
  const [hours, minutes, seconds] = timePart.split(':')

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}Z`
}

/**
 * 日付が今日より前のものであるかを判定
 * @param dateInput // Date オブジェクトまたは YYYY/MM/DD HH:mm:ss 形式の日付文字列
 * @returns 今日より前の日付であれば true を、それ以外の場合は false を返す
 */
export const isDateBeforeToday = (dateInput: Date | string): boolean => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  let inputDate: Date

  if (typeof dateInput === "string") {
    const isoDateString = toISOFormat(dateInput)
    inputDate = new Date(isoDateString)
  } else {
    inputDate = dateInput;
  }

  inputDate.setHours(0, 0, 0, 0)

  // 入力した日付が有効なものかを確認
  const isInputDateValid = !isNaN(inputDate.getTime())

  // 入力日付が有効で、今日より前の日付であれば true を、それ以外の場合は false を返す
  return isInputDateValid && inputDate.getTime() < today.getTime()
}

/**
 * 日付が来月の末日または翌々月の末日より後であるかを判定
 * @param dateInput // Date オブジェクトまたは YYYY/MM/DD HH:mm:ss 形式の日付文字列
 * @param isMainArena // boolean オプション。true の場合、翌々月の末日を基準にする
 * @param isSubArena // boolean オプション。true の場合、翌々月の末日を基準にする
 * @param isLocalOrganization // boolean オプション。true の場合、3カ月後の末日を基準にする
 * @returns 判定基準日より後の日付であれば true を、それ以外の場合は false を返す
 */
export const isDateAfterEndOfFollowingMonth = (
  dateInput: Date | string,
  isMainArena?: boolean,
  isSubArena?: boolean,
  isLocalOrganization?: boolean,
): boolean => {
  let inputDate: Date;

  if (typeof dateInput === "string") {
    const isoDateString = toISOFormat(dateInput);
    inputDate = new Date(isoDateString);
  } else {
    inputDate = dateInput;
  }

  inputDate.setHours(0, 0, 0, 0);

  // 入力した日付が有効なものかを確認
  const isInputDateValid = !isNaN(inputDate.getTime());
  // isLocalOrganization が true なら 3 か月後の末日を基準とする
  const monthOffset = isLocalOrganization ? 3 : (isMainArena || isSubArena ? 2 : 1);
  const endOfFollowingMonth = getEndOfMonthAfterMonths(monthOffset);

  // 入力日付が有効で、基準日より後の日付であれば true を、それ以外の場合は false を返す
  return isInputDateValid && inputDate.getTime() > endOfFollowingMonth.getTime();
}


/**
 * 日付をフォーマットする関数
 * @param dateString - 日付文字列
 * @returns フォーマットされた日付文字列
 */
const formatDateTime = (dateString:string) => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};
/**
 * 予約日時をフォーマットする関数
 * @param bookingDate - 予約の開始日時と終了日時を含むオブジェクト
 * @param bookingDate.start - 予約の開始日時、Date オブジェクトまたは YYYY-MM-DD HH:mm:ss 形式の日付文字列
 * @param bookingDate.end - 予約の終了日時、Date オブジェクトまたは YYYY-MM-DD HH:mm:ss 形式の日付文字列
 * @returns フォーマットされた予約日時オブジェクト
 * {
 *    start: 'YYYY/MM/DD HH:mm:ss',
 *    end: 'YYYY/MM/DD HH:mm:ss'
 * }
 */
export const formatBookingDate = (bookingDate:BookingDateInput) => {
  const formattedStart = formatDateTime(bookingDate.start);
  const formattedEnd = formatDateTime(bookingDate.end);
  
  return {
    start: formattedStart,
    end: formattedEnd,
  };
};


/**
 * 引数の日付が今日か過去であるか判定する
 * @param dateInput 日付（Date型またはYYYY-MM-DD形式の文字列）
 * @returns 引数の日付が今日か過去である場合はtrue、それ以外の場合はfalse
 */
export const isTodayOrPast = (dateInput: Date | string): boolean => {
  const today = new Date()
  // 今日の日付をYYYY-MM-DD形式に変換
  const todayStr = today.toISOString().slice(0, 10)

  let inputStr: string;
  if (typeof dateInput === 'string') {
    inputStr = dateInput
  } else {
    inputStr = dateInput.toISOString().slice(0, 10)
  }

  // 今日の日付と引数の日付を比較
  return inputStr <= todayStr
}


/**
 * 日付文字列から日部分を抽出する
 * @param dateStr 日付の文字列（YYYY-MM-DD形式）
 * @returns 日部分（頭の0が取り除かれる）
 */
export const extractDay = (dateStr: string): number => {
  const dayStr = dateStr.slice(-2) // 文字列の末尾の2文字を取得
  return parseInt(dayStr, 10) // 整数に変換し、先頭の0があれば取り除く
}

/**
 * 指定した日付の曜日を返す
 * @param dateStr 日付の文字列（YYYY-MM-DD形式）
 * @returns 曜日（英語）
 */
export const getDayOfWeek = (dateStr: string): string => {
  const date = new Date(dateStr)
  const days = ['日', '月', '火', '水', '木', '金', '土']
  return days[date.getDay()]
}

/**
 * 指定した日付が日曜日、土曜日、または平日であるかを返す
 * @param dateStr 日付の文字列（YYYY-MM-DD形式）
 * @returns 'Sunday'、'Saturday'、または 'Weekday'
 */
export const identifyDayType = (dateStr: string): string => {
  const date = new Date(dateStr)
  const day = date.getDay()

  if (day === 0) {
    return 'Sunday';
  } else if (day === 6) {
    return 'Saturday'
  } else {
    return 'Weekday'
  }
}

/**
 * 予約日数を返す
 * @param date1 // 利用開始日
 * @param date2 // 利用終了日
 * @return {number} // 予約日数
 */
export const calculateDaysBetween = (date1: string, date2: string): number => {
  // Date.parseメソッドを使用して日付文字列をミリ秒に変換
  const timeStamp1 = Date.parse(date1)
  const timeStamp2 = Date.parse(date2)

  // 二つの日付の差分をミリ秒で計算
  const diffInMilliSeconds = Math.abs(timeStamp2 - timeStamp1)

  // 差分をミリ秒から日に変換(1日 = 24*60*60*1000 ミリ秒)
  const diffInDays = diffInMilliSeconds / (24 * 60 * 60 * 1000)

  return Math.round(diffInDays) + 1
}

/**
 * 開始日と終了日を受け取り、その間のすべての日付を含む配列を返す
 * @param start 開始日
 * @param end 終了日
 * @returns ["YYYY-MM-DD", "YYYY-MM-DD", ....]
 */
export const getDatesBetween = (start: string, end: string): string[] => {
  const dateArray: string[] = []
  const currentDate = new Date(start)

  while (currentDate <= new Date(end)) {
    dateArray.push(currentDate.toISOString().split('T')[0]) // YYYY-MM-DD形式の文字列を追加
    currentDate.setDate(currentDate.getDate() + 1) // 日付を1日進める
  }

  return dateArray
}

/**
 * 予約の開始時間と終了時間を受け取り、利用時間を返す
 * @param time1  // 開始時間
 * @param time2  // 終了時間
 * @return 分単位の利用時間
 */
export const calculateMinutes = (time1: string, time2: string): number => {
  if (!time1 || !time2) return 0
  // 'hh:mm'形式をパースしてDateオブジェクトを作成
  const [hours1, minutes1] = time1.split(':').map(Number)
  const [hours2, minutes2] = time2.split(':').map(Number)

  const date1 = new Date()
  date1.setHours(hours1, minutes1)
  date1.setFullYear(1970, 0, 1) // 日付をリセット

  const date2 = new Date()
  date2.setHours(hours2, minutes2)
  date2.setFullYear(1970, 0, 1) // 日付をリセット

  // 2つの日付の差をミリ秒で取得し、ミリ秒を分に変換
  let diff = Math.abs((date2.getTime() - date1.getTime()) / 1000 / 60)

  // 結果を小数点第2位まで丸める
  diff = Math.round(diff * 100) / 100

  return diff
}

/**
 * 今日が15日以降か判定する
 * @returns 15日以降であればtrue、15日以前であればfalse
 */
export const isTodayAfter15th = (): boolean => {
  const currentDate = new Date()
  const currentDay = currentDate.getDate()
  return currentDay >= 15
}

/**
 * 指定した日付から今日までの日数を計算する
 * @param dateStr YYYY/MM/DD形式の文字列
 * @returns 今日からの日数
 */
export const getDaysFromToday = (dateStr: string): number => {
  const targetDate = new Date(dateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0) // 現在日付の時間を00:00:00.000にセット

  const diffInMilliseconds = today.getTime() - targetDate.getTime()

  // 1日は24時間*60分*60秒*1000ミリ秒 = 86400000ミリ秒
  const diffInDays = Math.round(diffInMilliseconds / 86400000)

  return diffInDays
}

/**
 * 今日の日付をYYYY-MM-DD形式の文字列で返す
 * @returns YYYY-MM-DD形式の文字列
 */
export const getToday = (): string => {
  const today = new Date()
  return formatDateToString(today, "-").split(" ")[0]
}

/**
 * 指定した日付をもとにN日後の日付を取得する
 * @param base 基準日 (YYYY-MM-DD形式の文字列)
 * @param daysToFuture 追加する日数
 * @returns N日後の日付 (YYYY-MM-DD 形式の文字列)
 */
export const getNthDayFrom = (base: string, daysToFuture: number): string => {
  const date = new Date(base)
  date.setDate(date.getDate() + daysToFuture) // 日数を追加
  return formatDateToString(date, "-").split(" ")[0]
}

/**
  * 指定した月数後の末日を返す
  * @param monthsToAdd 月数
  * @returns 末日
  */
export const getEndOfMonthAfterMonths = (monthsToAdd: number): Date => {
  // 今日の日付を取得
  const currentDate = new Date()
  // 翌月を取得
  currentDate.setMonth(currentDate.getMonth() + 1)
  // 指定された月数後を取得
  currentDate.setMonth(currentDate.getMonth() + monthsToAdd)
  // 翌月の最初の日を取得
  currentDate.setDate(1)
  // 末日を取得
  currentDate.setDate(currentDate.getDate() - 1)

  return currentDate
}

/**
 * Date型の日付をYYYY/MM/DD HH:mm:ss形式の文字列に変換する
 * @param date Date型の日付
 * @returns YYYY-MM-DD HH:mm:ss形式の文字列
 */
export const formatDateToString = (date: Date, type: "/" | "-" = "/"): string => {
  const year = date.getFullYear()
  const month = ('0' + (date.getMonth() + 1)).slice(-2)  // 月は0から始まるため、+1する
  const day = ('0' + date.getDate()).slice(-2)
  const hours = ('0' + date.getHours()).slice(-2)
  const minutes = ('0' + date.getMinutes()).slice(-2)
  const seconds = ('0' + date.getSeconds()).slice(-2)
  return `${year}${type}${month}${type}${day} ${hours}:${minutes}:${seconds}`
}

/**
 * HH:mm:ss形式の文字列を受け取り、現時刻より未来かどうかを返す
 * @param time HH:mm:ss形式の文字列
 * @returns 現時刻より未来であればtrue、過去であればfalse
 */
export const isTimeAfterNow = (time: string): boolean => {
  const [hours, minutes, seconds] = time.split(':').map(Number)
  const date = new Date()
  date.setHours(hours, minutes, seconds, 0)  // 時、分、秒を設定し、ミリ秒は0に設定

  const now = new Date()
  now.setMilliseconds(0)  // 現時刻のミリ秒を0に設定

  return date.getTime() > now.getTime()  // 秒単位までの比較
}

/**
 * 利用不可リストに引数のvariantIdを持つ料金テーブルの全てのバリエーションIDのリストを取得する
 * @param variantId // 料金テーブルのバリエーションID
 * @param data // 料金テーブルのデータ
 * @returns // 利用不可な料金テーブルのバリエーションIDのリスト
 */
export const getRateVariantIdsByVariantId = (variantId: string, data: NotForSalesQuery) => {
  // 戻り値となる配列を初期化
  let rateVariantIds: Array<string> = []

  // data内の各notForsalesエッジをループ処理
  data.notForSales.edges.forEach(({ node }) => {
    // 各エッジ内のproductsエッジをループ処理
    node.products.edges.forEach(productEdge => {
      // variantIdsに指定のvariantIdが存在する場合
      if (productEdge.node.variantIds.includes(variantId)) {
        // rateVariantIds配列に該当する料金テーブルのバリエーションIDを追加
        rateVariantIds = rateVariantIds.concat(node.rate.variants.edges.map(variantEdge => variantEdge.node.id))
      }
    })
  })

  // 生成したrateVariantIds配列を返す
  return rateVariantIds
}

/**
 * bookingStatusList から、お問い合わせ期間に該当する日付のみを配列で返す関数
 * @param bookingStatusList - 予約状況リスト（BookingStatusListQuery のデータ）
 * @param targetBooking - 該当する booking の種類を示す値（例: 5 = お問い合わせ期間）
 * @returns お問い合わせ期間に該当する日付の配列
 */
export const getContactRequiredDates = (
  bookingStatusList: BookingStatusListQuery,
  targetBooking: number
): string[] => {
  const datesSet = new Set<string>();

  // bookingStatusList から直接 productVariants を取得
  const { bookingStatusList: statusList } = bookingStatusList;

  // productVariants が存在するか確認
  if (statusList?.productVariants) {
    statusList.productVariants.forEach(variant => {
      // dataOfTheDays が存在するか確認
      if (variant?.dataOfTheDays) {
        variant.dataOfTheDays.forEach(day => {
          // bookings が存在し、targetBooking を含んでいるか確認
          if (day?.bookings?.includes(targetBooking)) {
            if (day.name) {
              datesSet.add(day.name);
            }
          }
        });
      }
    });
  }

  return Array.from(datesSet);
};